import { updateMyAvailability } from "@/lib/api/users";
import { getAuthUser, subscribeAuthStateChange } from "@/lib/api/token-store";

// WS-driven presence store. The backend derives online state from live staff WebSocket
// connections (PresenceHub) and pushes events that chat-realtime-service intercepts and
// forwards here:
//   delta:    { action:"presence",          userId, updates:{ online, availability } }
//   snapshot: { action:"presence.snapshot", userId:<self>, updates:{ online:[{userId, availability}] } }
// A fresh snapshot arrives on every (re)connect AND on every successful tenant user-object
// subscribe, so there is no polling fallback — the old POST /users/heartbeat loop is gone.
// Snapshots are idempotent full replaces; receiving two per connection is expected and safe.

export const AVAILABILITY_STATUSES = ["available", "busy", "meeting", "away", "dnd"];

export function normalizeAvailability(value) {
  const v = String(value || "").trim().toLowerCase();
  return AVAILABILITY_STATUSES.includes(v) ? v : "available";
}

let onlineIds = new Set();
/** @type {Record<string, string>} userId -> availability (online users; self always kept) */
let availabilityById = {};
// The caller's own status. Kept separately from the online map: it is a persisted user
// field that applies (and is settable) even while the socket is down.
let selfAvailability = "available";
// Outstanding local status picks. Presence carries no version, so a snapshot or a self-targeted
// delta landing mid-write describes the value we are in the middle of replacing — adopting it
// visibly reverts the pick in the status picker and flips DND notification muting with it. The
// backend now snapshots on subscribe and on demand, not only at connect, so this window is hit
// often enough to matter.
let selfAvailabilityWritesInFlight = 0;
const listeners = new Set();

// True while a local pick is still being persisted: server echoes about ourselves are stale.
function serverAvailabilityIsStaleFor(userId) {
  return selfAvailabilityWritesInFlight > 0 && userId === selfUserId();
}

function notify() {
  for (const listener of listeners) {
    try {
      listener(onlineIds);
    } catch {
      // A misbehaving listener must not break the store.
    }
  }
}

function selfUserId() {
  return String(getAuthUser()?.id || "").trim();
}

function seedSelfFromAuthUser() {
  selfAvailability = normalizeAvailability(getAuthUser()?.availability);
}
seedSelfFromAuthUser();

subscribeAuthStateChange(() => {
  const user = getAuthUser();
  if (!user) {
    // Logout: forget everyone (a login on this device re-snapshots via the socket).
    onlineIds = new Set();
    availabilityById = {};
    selfAvailability = "available";
    notify();
    return;
  }
  seedSelfFromAuthUser();
  notify();
});

/**
 * Applies a presence event forwarded by chat-realtime-service.
 * @param {{ kind: 'snapshot', online: Array<{userId: string, availability?: string}> }
 *       | { kind: 'delta', userId: string, online: boolean, availability?: string }} event
 */
export function applyPresenceRealtime(event) {
  if (!event || typeof event !== "object") return;
  if (event.kind === "snapshot") {
    const nextIds = new Set();
    const nextAvailability = {};
    const self = selfUserId();
    for (const entry of Array.isArray(event.online) ? event.online : []) {
      const id = String(entry?.userId || "").trim();
      if (!id) continue;
      nextIds.add(id);
      // A snapshot is a full replace, so our own row has to be carried over rather than
      // skipped while a pick is in flight — dropping it would blank the dot instead.
      nextAvailability[id] = serverAvailabilityIsStaleFor(id)
        ? selfAvailability
        : normalizeAvailability(entry?.availability);
    }
    onlineIds = nextIds;
    availabilityById = nextAvailability;
    if (self && nextAvailability[self] && !serverAvailabilityIsStaleFor(self)) {
      selfAvailability = nextAvailability[self];
    }
    notify();
    return;
  }
  if (event.kind === "delta") {
    const id = String(event.userId || "").trim();
    if (!id) return;
    const stale = serverAvailabilityIsStaleFor(id);
    const nextAvailability = { ...availabilityById };
    if (event.online) {
      if (!onlineIds.has(id)) {
        onlineIds = new Set(onlineIds);
        onlineIds.add(id);
      }
      if (!stale) nextAvailability[id] = normalizeAvailability(event.availability);
    } else {
      if (onlineIds.has(id)) {
        onlineIds = new Set(onlineIds);
        onlineIds.delete(id);
      }
      // Keep the last known availability for the self row; drop others to stay lean.
      if (id !== selfUserId()) {
        delete nextAvailability[id];
      } else if (event.availability && !stale) {
        nextAvailability[id] = normalizeAvailability(event.availability);
      }
    }
    availabilityById = nextAvailability;
    if (id === selfUserId() && event.availability && !stale) {
      selfAvailability = normalizeAvailability(event.availability);
    }
    notify();
  }
}

/** Current online user ids (a Set). */
export function getOnlineUserIds() {
  return onlineIds;
}

export function isUserOnline(userId) {
  const id = String(userId || "").trim();
  return Boolean(id) && onlineIds.has(id);
}

/** userId -> availability for online users (stable reference until a change). */
export function getAvailabilityMap() {
  return availabilityById;
}

export function getUserAvailability(userId) {
  const id = String(userId || "").trim();
  if (id && id === selfUserId()) return selfAvailability;
  return normalizeAvailability(availabilityById[id]);
}

/** The caller's own status — drives the DND notification mute and the status picker. */
export function getSelfAvailability() {
  return selfAvailability;
}

/**
 * Sets the caller's availability: optimistic locally (every dot re-renders at once),
 * then persisted via PUT /users/me/availability — the backend broadcasts the change to
 * colleagues. Reverts and rethrows on failure so the caller can toast.
 */
export async function setSelfAvailability(next) {
  const value = normalizeAvailability(next);
  const previous = selfAvailability;
  if (value === previous) return value;
  selfAvailability = value;
  const self = selfUserId();
  if (self) {
    availabilityById = { ...availabilityById, [self]: value };
  }
  selfAvailabilityWritesInFlight += 1;
  notify();
  try {
    await updateMyAvailability({ availability: value });
    return value;
  } catch (error) {
    // Only the last writer may revert: an older failed pick must not stomp a newer one.
    if (selfAvailabilityWritesInFlight === 1) {
      selfAvailability = previous;
      if (self) {
        availabilityById = { ...availabilityById, [self]: previous };
      }
      notify();
    }
    throw error;
  } finally {
    selfAvailabilityWritesInFlight -= 1;
  }
}

/** Subscribe to presence changes; the listener receives the online-id Set. Returns an unsubscribe. */
export function subscribePresence(listener) {
  if (typeof listener !== "function") return () => {};
  listeners.add(listener);
  return () => listeners.delete(listener);
}
